import { useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFollowList } from '@/hooks/useFollowList';
import { useMuteList } from '@/hooks/useMuteList';
import { useBlockList } from '@/hooks/useBlockList';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface UserActionButtonsProps {
  pubkey: string;
  displayName?: string;
}

function MoreIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
  );
}

export function UserActionButtons({ pubkey, displayName = 'this user' }: UserActionButtonsProps) {
  const { user } = useCurrentUser();
  const { isFollowing, follow, unfollow, isLoading: followLoading } = useFollowList();
  const { isMuted, mute, unmute, isLoading: muteLoading } = useMuteList();
  const { isBlocked, block, unblock, isLoading: blockLoading } = useBlockList();

  const [pendingAction, setPendingAction] = useState<'mute' | 'unmute' | 'block' | 'unblock' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Don't show for own profile or when not logged in
  if (!user || user.pubkey === pubkey) return null;

  const following = isFollowing(pubkey);
  const muted = isMuted(pubkey);
  const blocked = isBlocked(pubkey);
  const isInitializing = followLoading || muteLoading || blockLoading;

  async function handleFollowToggle() {
    setActionLoading(true);
    try {
      if (following) {
        await unfollow(pubkey);
      } else {
        await follow(pubkey);
      }
    } finally {
      setActionLoading(false);
    }
  }

  async function handleConfirmedAction() {
    if (!pendingAction) return;
    setActionLoading(true);
    try {
      if (pendingAction === 'mute') await mute(pubkey);
      else if (pendingAction === 'unmute') await unmute(pubkey);
      else if (pendingAction === 'block') await block(pubkey);
      else if (pendingAction === 'unblock') await unblock(pubkey);
    } finally {
      setActionLoading(false);
      setPendingAction(null);
    }
  }

  const confirmConfig: Record<
    'mute' | 'unmute' | 'block' | 'unblock',
    { title: string; description: string; actionLabel: string; destructive?: boolean }
  > = {
    mute: {
      title: `Mute ${displayName}?`,
      description: `You won't see ${displayName}'s content in your feeds. They won't be notified. You can unmute them at any time.`,
      actionLabel: 'Mute',
      destructive: true,
    },
    unmute: {
      title: `Unmute ${displayName}?`,
      description: `${displayName}'s content will appear in your feeds again.`,
      actionLabel: 'Unmute',
    },
    block: {
      title: `Block ${displayName}?`,
      description: `${displayName} will be added to your block list. Their content will be hidden and they won't be able to interact with you. You can unblock them at any time.`,
      actionLabel: 'Block',
      destructive: true,
    },
    unblock: {
      title: `Unblock ${displayName}?`,
      description: `${displayName} will be removed from your block list.`,
      actionLabel: 'Unblock',
    },
  };

  const confirm = pendingAction ? confirmConfig[pendingAction] : null;

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Follow / Unfollow button */}
        <button
          onClick={handleFollowToggle}
          disabled={isInitializing || actionLoading}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full font-medium text-sm transition-all disabled:opacity-50"
          style={
            following
              ? {
                  background: 'transparent',
                  border: '1.5px solid var(--gb-border)',
                  color: 'var(--text-primary)',
                }
              : {
                  background: 'var(--gb-accent)',
                  border: '1.5px solid var(--gb-accent)',
                  color: '#fff',
                }
          }
          onMouseEnter={e => {
            if (following) {
              e.currentTarget.style.borderColor = '#ef4444';
              e.currentTarget.style.color = '#ef4444';
              e.currentTarget.querySelector('.follow-label')!.textContent = 'Unfollow';
            }
          }}
          onMouseLeave={e => {
            if (following) {
              e.currentTarget.style.borderColor = 'var(--gb-border)';
              e.currentTarget.style.color = 'var(--text-primary)';
              e.currentTarget.querySelector('.follow-label')!.textContent = 'Following';
            }
          }}
        >
          {!following && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          )}
          {following && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="follow-check">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
            </svg>
          )}
          <span className="follow-label">{following ? 'Following' : 'Follow'}</span>
        </button>

        {/* More actions dropdown (mute, block) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center justify-center w-9 h-9 rounded-full transition-colors"
              style={{ border: '1.5px solid var(--gb-border)', color: 'var(--text-secondary)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.07)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              aria-label="More actions"
            >
              <MoreIcon />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {/* Mute / Unmute */}
            {muted ? (
              <DropdownMenuItem
                onClick={() => setPendingAction('unmute')}
                className="gap-2 cursor-pointer"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="opacity-70">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                </svg>
                Unmute
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={() => setPendingAction('mute')}
                className="gap-2 cursor-pointer"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="opacity-70">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM19 12c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM3 9v6h4l5 5V4L7 9H3z" />
                </svg>
                Mute
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            {/* Block / Unblock */}
            {blocked ? (
              <DropdownMenuItem
                onClick={() => setPendingAction('unblock')}
                className="gap-2 cursor-pointer"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="opacity-70">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
                </svg>
                Unblock
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={() => setPendingAction('block')}
                className="gap-2 cursor-pointer text-red-500 focus:text-red-500"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.68L5.68 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.68L18.32 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z" />
                </svg>
                Block
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Confirmation dialog */}
      <AlertDialog open={!!pendingAction} onOpenChange={open => { if (!open) setPendingAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmedAction}
              disabled={actionLoading}
              className={confirm?.destructive ? 'bg-red-600 hover:bg-red-700 focus:ring-red-600' : ''}
            >
              {actionLoading ? 'Please wait…' : confirm?.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
